/**
 * Oqto OAuth client — shared by the web app's connect routes and the
 * runtime's token minting.
 *
 * One Squidler-owned application per environment, registered in Oqto's
 * developer portal:
 *   OQTO_OAUTH_CLIENT_ID
 *   OQTO_OAUTH_CLIENT_SECRET
 *   OQTO_OAUTH_REDIRECT_URI
 *
 * Oqto's model is in two phases, and the split is what makes this the
 * simplest of the accounting integrations to keep alive
 * (api.oqto.se/docs/v1, "Authentication"):
 *
 *  1. ACTIVATION yields a `company_id` and nothing else. Either the
 *     authorization-code flow below (`buildAuthUrl` → `exchangeCode`), or
 *     the user activates the application inside Oqto and pastes the company
 *     id Oqto then shows them. Both end in the same uuid.
 *
 *  2. ACCESS is a client-credentials grant: our client id and secret plus
 *     the company id mint a ten-minute bearer token (`mintCompanyToken`).
 *     There is no refresh token. Nothing per connection can be spent,
 *     rotated or lost, minting is safe to retry and safe to race, and any
 *     number of agents can serve one company — the properties Fortnox only
 *     reached after abandoning its refresh tokens.
 *
 * Two endpoints on two hosts, and that is Oqto's design rather than a typo:
 * the code exchange goes to the app host, the token mint to the API host.
 *
 * SCOPES: consent asks for all of them, the mint takes a subset. The scope
 * names live in `scopes.ts` (transcribed from the developer portal — the
 * OpenAPI document does not list them). `buildAuthUrl` requests every scope
 * so a company authorizes the integration once; `mintCompanyToken` then
 * takes the scopes of the areas a connection includes, so the token itself
 * is narrowed. Read-vs-write within an area is still our own gate
 * (`@shared/oqto/policy.ts`), as for Fortnox: the scopes have no such split.
 */

import { OQTO_ALL_SCOPES } from "./scopes";

/** Consent and the code exchange live on the app host. */
const AUTH_URL = "https://app.oqto.se/oauth/authorize";
const CODE_TOKEN_URL = "https://app.oqto.se/oauth/token";
/** Access tokens are minted on the API host. */
const ACCESS_TOKEN_URL = "https://api.oqto.se/oauth/token";

interface OAuthConfig {
  readonly clientId: string;
  readonly clientSecret: string;
  readonly redirectUri: string;
}

/** Read the env triple, or null when it is not fully set. Both the getter and
 *  the configured-check go through here so the var names appear once. */
const readConfig = (): OAuthConfig | null => {
  const clientId = process.env.OQTO_OAUTH_CLIENT_ID;
  const clientSecret = process.env.OQTO_OAUTH_CLIENT_SECRET;
  const redirectUri = process.env.OQTO_OAUTH_REDIRECT_URI;
  if (!clientId || !clientSecret || !redirectUri) return null;
  return { clientId, clientSecret, redirectUri };
};

const getConfig = (): OAuthConfig => {
  const config = readConfig();
  if (!config) {
    throw new Error(
      "Oqto OAuth not configured — set OQTO_OAUTH_CLIENT_ID, " +
      "OQTO_OAUTH_CLIENT_SECRET, and OQTO_OAUTH_REDIRECT_URI",
    );
  }
  return config;
};

export const isOqtoConfigured = (): boolean => readConfig() !== null;

// --- Auth URL ---

interface BuildAuthUrlArgs {
  readonly state: string;
  /**
   * The Oqto company to pre-select on the consent screen, when re-authorizing
   * one we already hold. Omit for a first connection — the user picks then.
   * A pre-selection only, which the user can still change, so the caller
   * records it as an expectation and checks the callback against it.
   */
  readonly companyId?: string;
}

/**
 * Build the consent URL.
 *
 * `state` is optional to Oqto and mandatory to us: it is persisted
 * server-side in `oauth_states`, bound to the initiating user and instance,
 * and consumed single-use on callback. Every scope is requested — see the
 * header: the per-connection narrowing happens at mint time, not here.
 */
export const buildAuthUrl = ({ state, companyId }: BuildAuthUrlArgs): string => {
  if (state.length === 0) throw new Error("Oqto authorize needs a non-empty state");
  const { clientId, redirectUri } = getConfig();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: OQTO_ALL_SCOPES.join(" "),
    state,
  });
  if (companyId) params.set("company_id", companyId);
  return `${AUTH_URL}?${params.toString()}`;
};

// --- Errors ---

export class OqtoOAuthError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "OqtoOAuthError";
  }
}

/**
 * "invalid_client" from the mint endpoint. Documented as "Invalid client_id
 * or company has not authorized this client", and MEASURED (2026-09-24) to
 * cover a wrong client secret as well — the documented `invalid_grant` for
 * that case never comes, and the body carries no `error_description` to
 * tell the cases apart. So this is "Oqto would not mint for this company
 * with these credentials", not proof of the company's decision; the runtime
 * (`tools/oqto/tokens.ts`) decides what that means for the row, and the web
 * reports it to the connecting user as "not activated" while noting the
 * other possibility in its log.
 */
export const isCompanyNotAuthorized = (err: unknown): boolean =>
  err instanceof OqtoOAuthError && err.code === "invalid_client";

const postForm = async (url: string, body: URLSearchParams): Promise<Record<string, unknown>> => {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body,
  });
  const parsed: unknown = await res.json().catch(() => ({}));
  const payload = (parsed ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const code = typeof payload.error === "string" ? payload.error : `http_${res.status}`;
    const description = typeof payload.error_description === "string"
      ? payload.error_description
      : `HTTP ${res.status}`;
    throw new OqtoOAuthError(code, description);
  }
  return payload;
};

// --- Activation: code → company id ---

/**
 * Exchange an authorization code (single use, five minutes) for the company
 * id it was issued for. That is the whole response: no token comes back
 * from this step.
 */
export const exchangeCode = async (code: string): Promise<{ readonly company_id: string }> => {
  const { clientId, clientSecret, redirectUri } = getConfig();
  const payload = await postForm(CODE_TOKEN_URL, new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    client_secret: clientSecret,
    code,
    // Compared byte for byte against the authorize request.
    redirect_uri: redirectUri,
  }));
  if (typeof payload.company_id !== "string" || payload.company_id.length === 0) {
    // A 200 without the one field it exists to carry. Keyed on a guess, a
    // connection would shadow a real one.
    throw new OqtoOAuthError("malformed_token_response", "code exchange returned no company_id");
  }
  return { company_id: payload.company_id };
};

// --- Access: company id → bearer token ---

export interface OqtoAccessToken {
  readonly access_token: string;
  /** Seconds. Documented default 600. */
  readonly expires_in: number;
}

/**
 * Mint an access token for one company with `grant_type=client_credentials`.
 *
 * Idempotent and side-effect free on Oqto's side, so it may be retried and
 * raced freely. Ten minutes of life, then mint again; the runtime caches per
 * company and scope set (`tools/oqto/tokens.ts`).
 *
 * `scopes` narrows the token to a subset of what the company authorized —
 * the scopes of the areas a connection includes. Omitted, the token carries
 * everything the company authorized; the web uses that once, to name a
 * company it is connecting.
 */
export const mintCompanyToken = async (
  companyId: string,
  scopes?: readonly string[],
): Promise<OqtoAccessToken> => {
  const { clientId, clientSecret } = getConfig();
  const payload = await postForm(ACCESS_TOKEN_URL, new URLSearchParams({
    grant_type: "client_credentials",
    client_id: clientId,
    client_secret: clientSecret,
    company_id: companyId,
    ...(scopes && scopes.length > 0 ? { scope: scopes.join(" ") } : {}),
  }));
  if (typeof payload.access_token !== "string" || payload.access_token.length === 0) {
    throw new OqtoOAuthError("malformed_token_response", "client_credentials response missing access_token");
  }
  return {
    access_token: payload.access_token,
    expires_in: typeof payload.expires_in === "number" && payload.expires_in > 0 ? payload.expires_in : 600,
  };
};
